import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LOGO_URL } from "@/components/Logo";
import {
  LayoutDashboard, Headphones, Users, Car, Building2, Bus,
  ClipboardList, BarChart3, Settings, LogOut, Bell, Plug
} from "lucide-react";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard", testId: "admin-nav-dashboard" },
  { to: "/admin/dispatch", icon: Headphones, label: "Dispatch", testId: "admin-nav-dispatch" },
  { to: "/admin/bookings", icon: ClipboardList, label: "Bookings", testId: "admin-nav-bookings" },
  { to: "/admin/employees", icon: Users, label: "Employees", testId: "admin-nav-employees" },
  { to: "/admin/drivers", icon: Building2, label: "Drivers", testId: "admin-nav-drivers" },
  { to: "/admin/fleet", icon: Car, label: "Fleet", testId: "admin-nav-fleet" },
  { to: "/admin/bus-routes", icon: Bus, label: "Bus Routes", testId: "admin-nav-bus" },
  { to: "/admin/analytics", icon: BarChart3, label: "Analytics", testId: "admin-nav-analytics" },
  { to: "/admin/integrations", icon: Plug, label: "Integrations", testId: "admin-nav-integrations" },
  { to: "/admin/settings", icon: Settings, label: "Settings", testId: "admin-nav-settings" },
];

export default function AdminShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-slate-100 flex items-center gap-3">
          <img src={LOGO_URL} alt="TESLAM" className="h-10" />
          <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold">Admin Console</p>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive ? "bg-primary text-white shadow-md shadow-primary/30" : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <n.icon size={18} />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-100">
          <div className="flex items-center gap-3 px-3 py-2 rounded-xl">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-primary-700 text-white flex items-center justify-center font-bold">
              {user?.name?.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 text-sm truncate" data-testid="admin-user-name">{user?.name}</p>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{user?.role}</p>
            </div>
            <button data-testid="admin-logout" onClick={() => { logout(); nav("/login/admin", { replace: true }); }} className="text-slate-400 hover:text-rose-600">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 flex flex-col overflow-x-auto">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-10">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <span>ENEC</span>
            <span>/</span>
            <span className="text-slate-900 font-semibold">Transport Operations</span>
          </div>
          <div className="flex items-center gap-3">
            <button className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600 relative">
              <Bell size={18} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-primary" />
            </button>
            <div className="text-xs font-mono text-slate-400">
              {new Date().toLocaleString("en-AE", { dateStyle: "medium", timeStyle: "short" })}
            </div>
          </div>
        </header>
        <div className="flex-1 p-6 overflow-y-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
